import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';

export interface IFreightTypeEntityAttributes extends IModelAttributes {
	label: string;
	minimumCubic: number;
	minimumWeight: number;
	standardWidth: number;
	standardHeight: number;
	standardLength: number;
	standardDescription: string;
	disabled: boolean;

	freightDetails: Array<Models.FreightDetailEntity | Models.IFreightDetailEntityAttributes>;
	freightCategoryId?: string;
	freightCategory?: Models.FreightCategoryEntity | Models.IFreightCategoryEntityAttributes;
	staticPricing?: Models.PriceEntity | Models.IPriceEntityAttributes;
}
@entity('FreightTypeEntity', 'Freight Type')
export default class FreightTypeEntity extends Model implements IFreightTypeEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * The display label for the freight type (e.g. pallet, container).
	 */
	@observable
	@attribute<FreightTypeEntity, string>()
	public label: string;

	/**
	 * The minimum cubic for the freight type.
	 */
	// This field is not needed as the cubic is calculated from the width, length and height.
	@observable
	@attribute<FreightTypeEntity, number>()
	public minimumCubic: number;

	/**
	 * The minimum weight for the freight type.
	 */
	@observable
	@attribute<FreightTypeEntity, number>()
	public minimumWeight: number;

	/**
	 * The standard width of the freight type.
	 */
	@observable
	@attribute<FreightTypeEntity, number>()
	public standardWidth: number;

	/**
	 * The standard height of the freight type.
	 */
	@observable
	@attribute<FreightTypeEntity, number>()
	public standardHeight: number;

	/**
	 * The standard length of the freight type.
	 */
	@observable
	@attribute<FreightTypeEntity, number>()
	public standardLength: number;

	/**
	 * The standard length of the freight type.
	 */
	@observable
	@attribute<FreightTypeEntity, string>()
	public standardDescription: string;

	/**
	 * Whether or not this freight type is disabled.
	 */
	@observable
	@attribute<FreightTypeEntity, boolean>()
	public disabled: boolean;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public freightDetails: Models.FreightDetailEntity[] = [];

	@observable
	@attribute()
	public freightCategoryId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public freightCategory: Models.FreightCategoryEntity;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public staticPricing?: Models.PriceEntity;

	constructor(attributes?: Partial<IFreightTypeEntityAttributes>) {
		super(attributes);

		this.freightDetails = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IFreightTypeEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.label !== undefined) {
				this.label = attributes.label;
			}
			if (attributes.minimumCubic !== undefined) {
				this.minimumCubic = attributes.minimumCubic;
			}
			if (attributes.minimumWeight !== undefined) {
				this.minimumWeight = attributes.minimumWeight;
			}
			if (attributes.standardWidth !== undefined) {
				this.standardWidth = attributes.standardWidth;
			}
			if (attributes.standardHeight !== undefined) {
				this.standardHeight = attributes.standardHeight;
			}
			if (attributes.standardLength !== undefined) {
				this.standardLength = attributes.standardLength;
			}
			if (attributes.standardDescription !== undefined) {
				this.standardDescription = attributes.standardDescription;
			}
			if (attributes.disabled !== undefined) {
				this.disabled = attributes.disabled;
			}
			if (attributes.freightDetails !== undefined && Array.isArray(attributes.freightDetails)) {
				if (!this.freightDetails) {
					this.freightDetails = [];
				}
				for (const model of attributes.freightDetails) {
					if (model instanceof Models.FreightDetailEntity) {
						this.freightDetails.push(model);
					} else {
						this.freightDetails.push(new Models.FreightDetailEntity(model));
					}
				}
			}
			if (attributes.freightCategoryId !== undefined) {
				this.freightCategoryId = attributes.freightCategoryId;
			}
			if (attributes.freightCategory !== undefined) {
				if (attributes.freightCategory === null) {
					this.freightCategory = attributes.freightCategory;
				} else if (attributes.freightCategory instanceof Models.FreightCategoryEntity) {
					this.freightCategory = attributes.freightCategory;
					this.freightCategoryId = attributes.freightCategory.id;
				} else {
					this.freightCategory = new Models.FreightCategoryEntity(attributes.freightCategory);
					this.freightCategoryId = this.freightCategory.id;
				}
			}
			if (attributes.staticPricing !== undefined) {
				if (attributes.staticPricing === null) {
					this.staticPricing = attributes.staticPricing;
				} else if (attributes.staticPricing instanceof Models.PriceEntity) {
					this.staticPricing = attributes.staticPricing;
				} else {
					this.staticPricing = new Models.PriceEntity(attributes.staticPricing);
				}
			}
		}
	}
}
